import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import styles from './FAQAccordion.module.scss';
export interface FAQItem {
  /** Question text shown in the accordion trigger. Accepts a string or any React node. */
  question: ReactNode;
  /** Answer content revealed when the item is open. */
  answer: ReactNode;
}
export type FAQAccordionVariant = 'compact' | 'default' | 'large';

/**
 * The element around each question's button: `none` (the default) or a
 * heading level, for a list whose questions are headings in the page's
 * outline (the WAI-ARIA accordion pattern's heading-wrapped trigger; Phase 5
 * PR 5.8: the Vibe Coding FAQ and curriculum keep their `h3`s). The heading
 * adds no margin or type of its own.
 */
export type FAQAccordionQuestionAs = 'none' | 'h2' | 'h3' | 'h4';
export interface FAQAccordionProps {
  /** The questions, in order. An empty list renders nothing. */
  items: readonly FAQItem[];
  /** Wraps each question's button in this heading level (`none` by default). */
  questionAs?: FAQAccordionQuestionAs;
  /** Layout scale. `large` mirrors the Shaq Scholars FAQ treatment. */
  variant?: FAQAccordionVariant;
}
function FAQAccordion(t0) {
  const $ = _c(17);
  const {
    items,
    questionAs: t1,
    variant: t2
  } = t0;
  const questionAs = t1 === undefined ? "none" : t1;
  const variant = t2 === undefined ? "default" : t2;
  const [openIndex, setOpenIndex] = useState(null);
  const accordionId = useId();
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = i => setOpenIndex(prev => prev === i ? null : i);
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const toggle = t3;
  if (items.length === 0) {
    return null;
  }
  const Heading = questionAs === "none" ? null : questionAs;
  const t4 = styles[`helios-faq--${variant}`];
  let t5;
  if ($[1] !== t4) {
    t5 = classNames(styles["helios-faq"], t4);
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== Heading || $[4] !== accordionId || $[5] !== items || $[6] !== openIndex) {
    let t7;
    if ($[8] !== Heading || $[9] !== accordionId || $[10] !== openIndex) {
      t7 = (item, i_0) => {
        const buttonId = `${accordionId}-button-${i_0}`;
        const panelId = `${accordionId}-panel-${i_0}`;
        const button = <button id={buttonId} type="button" className={classNames(styles["helios-faq__header"], {
          [styles["helios-faq__header--open"]]: openIndex === i_0
        })} onClick={() => toggle(i_0)} aria-expanded={openIndex === i_0} aria-controls={panelId}>{item.question}</button>;
        return <div key={i_0} className={styles["helios-faq__item"]}>{Heading ? <Heading className={styles["helios-faq__heading"]}>{button}</Heading> : button}<div id={panelId} role="region" aria-labelledby={buttonId} className={styles["helios-faq__body"]} hidden={openIndex !== i_0}>{item.answer}</div></div>;
      };
      $[8] = Heading;
      $[9] = accordionId;
      $[10] = openIndex;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    t6 = items.map(t7);
    $[3] = Heading;
    $[4] = accordionId;
    $[5] = items;
    $[6] = openIndex;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = <div className={styles["helios-faq__list"]}>{t6}</div>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t5 || $[15] !== t7) {
    t8 = <div className={t5}>{t7}</div>;
    $[14] = t5;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}
export default FAQAccordion;